import { AppThemeToggle } from '@playbooks/components/app/app-theme-toggle';
import { MktLogo } from '@playbooks/components/mkt/mkt-logo';
import { useClient } from '@playbooks/contexts';
import { FabIcons } from '@playbooks/icons';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

const footerGroups = [
	{
		title: 'Company',
		links: [
			{ label: 'About', href: '/about' },
			{ label: 'Product', href: '/product' },
			{ label: 'Pricing', href: '/pricing' },
			{ label: 'Blog', href: '/blog' },
			{ label: 'Contact', href: '/contact' },
		],
	},
	{
		title: 'Explore',
		links: [
			{ label: 'Collections', href: '/explore/collections' },
			{ label: 'Categories', href: '/explore/categories' },
			{ label: 'Workspaces', href: '/workspaces' },
		],
	},
	{
		title: 'Resources',
		links: [
			{ label: 'Docs', href: '/docs' },
			{ label: 'Reference', href: '/reference' },
			{ label: 'GitHub', href: 'https://github.com/playbooks-hq', external: true },
			{ label: 'Changelog', href: '/blog/changelog' },
			{ label: 'Status', href: 'https://playbooks.betteruptime.com', external: true },
		],
	},
	{
		title: 'Legal',
		links: [
			{ label: 'Terms', href: '/terms' },
			{ label: 'Privacy', href: '/privacy' },
		],
	},
];

const socialLinks = [
	{ label: 'X', href: 'https://x.com/playbooks_hq', icon: FabIcons.fabXTwitter },
	{ label: 'GitHub', href: 'https://github.com/playbooks-hq', icon: FabIcons.fabGithub },
	{ label: 'Slack', href: 'https://slack.playbooks.ai', icon: FabIcons.fabSlack },
	{ label: 'npm', href: 'https://npmjs.com/package/@playbooks/cli', icon: FabIcons.fabNpm },
];

const MktFooter = () => {
	const { platformHref } = useClient();

	// Render
	return (
		<footer className='relative w-full'>
			<div className='mx-auto grid w-full max-w-xxl gap-10 px-6 py-12 md:grid-cols-[minmax(0,1.25fr)_minmax(0,2fr)] lg:gap-16 lg:px-8 lg:py-16'>
				<div className='flex-column max-w-sm items-start gap-4'>
					<MktLogo alt='Playbooks' />
					<p className='text-sm leading-6 text-foreground-body'>
						Playbooks helps developers accelerate their workflow, share knowledge, and monetize their expertise in the
						agentic era.
					</p>
					<ul className='flex-start gap-1' aria-label='Playbooks social links'>
						{socialLinks.map(link => (
							<li key={link.label}>
								<a
									aria-label={link.label}
									target='_blank'
									rel='noreferrer'
									href={link.href}
									className='flex-middle-inline size-8 rounded-control text-foreground-body transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'>
									<StructuralIcon icon={link.icon} className='size-4' />
								</a>
							</li>
						))}
					</ul>
				</div>

				<nav aria-label='Footer' className='grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4'>
					{footerGroups.map(group => (
						<div key={group.title}>
							<h2 className='type-label mb-3'>{group.title}</h2>
							<ul className='flex-column items-start gap-2.5'>
								{group.links.map(link => (
									<li key={link.label}>
										<a
											href={link.external ? link.href : platformHref(link.href)}
											target={link.external ? '_blank' : undefined}
											rel={link.external ? 'noopener noreferrer' : undefined}
											className='text-sm text-foreground-body transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus'>
											{link.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					))}
				</nav>
			</div>

			<div className='border-t'>
				<div className='mx-auto flex-column w-full max-w-xxl gap-4 px-6 py-4 sm:flex-between lg:px-8'>
					<div className='flex-start flex-wrap gap-x-8 gap-y-2'>
						<p className='text-sm text-foreground-body'>{new Date().getFullYear()} © Playbooks, Inc</p>
						<iframe
							title='Betterstack status'
							src='https://playbooks.betteruptime.com/badge?theme=light'
							className='h-[30px] w-[200px] border-0 dark:hidden'
						/>
						<iframe
							title='Betterstack status dark'
							src='https://playbooks.betteruptime.com/badge?theme=dark'
							className='hidden h-[30px] w-[200px] border-0 dark:block'
						/>
					</div>
					<AppThemeToggle compact />
				</div>
			</div>
		</footer>
	);
};

export { MktFooter };
